import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "@/components/Motion";
import { useCategories } from "@/context/CategoriesContext";
import { imgSrc } from "@/lib/imgSrc";

const Chips = ({ cats, className = "" }) => {
  const { colors } = useCategories();
  return (cats || []).length > 0 && (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {cats.slice(0, 4).map((cat) => (
        <span
          key={cat}
          style={{ borderColor: colors[cat] || "#888", color: colors[cat] || "#aaa" }}
          className="font-grotesk font-medium text-[9px] uppercase tracking-[0.12em] border px-2 py-1"
        >
          {cat}
        </span>
      ))}
    </div>
  );
};

/* Editorial Index — text rows, photo floats in on hover (used for photoless calls on the board) */
export function BoardIndex({ castings }) {
  const [hovered, setHovered] = useState(null);
  const active = castings.find((c) => c.id === hovered);
  return (
    <div className="border-t border-white/10" data-testid="castings-grid-text">
      {castings.map((c, i) => (
        <Reveal key={c.id} delay={Math.min(i, 6) * 0.05}>
          <Link
            to={`/castings/${c.slug || c.id}`}
            data-testid={`casting-card-${c.id}`}
            onMouseEnter={() => setHovered(c.id)}
            onMouseLeave={() => setHovered(null)}
            className="group grid grid-cols-12 items-baseline gap-x-6 gap-y-3 px-2 sm:px-4 py-10 border-b border-white/10 hover:bg-white/5 transition-colors duration-300"
          >
            <p className="col-span-12 md:col-span-2 overline-label !text-[10px]">{c.brand}</p>
            <h3 className="col-span-12 md:col-span-7 font-display font-light text-4xl sm:text-5xl tracking-tighter leading-[1.05] group-hover:text-accent transition-colors duration-300">
              {!c.image_url && <span className="text-accent">— </span>}
              {c.title}
            </h3>
            <div className="col-span-12 md:col-span-3 flex items-center md:justify-end gap-6">
              {c.external_url && (
                <span data-testid={`open-call-badge-${c.id}`} className="font-grotesk text-[9px] font-semibold uppercase tracking-[0.2em] text-accent border border-accent/60 px-2 py-1">
                  Free to apply ↗
                </span>
              )}
              <span className="font-grotesk text-xs uppercase tracking-[0.2em] text-white/60">{c.city}</span>
              <span className="font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-white/90">{c.compensation || "Rate TBD"}</span>
              <ArrowRight size={18} className="shrink-0 text-white/30 group-hover:text-accent group-hover:translate-x-1 transition-all duration-300" />
            </div>
          </Link>
        </Reveal>
      ))}
      <AnimatePresence>
        {active?.image_url && (
          <motion.div
            key={active.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="hidden lg:block fixed right-12 top-1/2 -translate-y-1/2 z-40 w-64 aspect-[3/4] pointer-events-none"
            data-testid="floating-preview"
          >
            <img src={imgSrc(active.image_url)} alt="" className="w-full h-full object-cover" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Gallery Masonry — contact-sheet moodboard (board + homepage) */
const MASONRY_RATIOS = ["aspect-[3/4]", "aspect-[4/5]", "aspect-[2/3]"];

export function BoardMasonry({ castings }) {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-10" data-testid="castings-grid">
      {castings.map((c, i) => (
        <div key={c.id} className="break-inside-avoid mb-12">
          <Reveal delay={(i % 3) * 0.06}>
            <Link to={`/castings/${c.slug || c.id}`} data-testid={`casting-card-${c.id}`} className="group block">
              <div className={`${MASONRY_RATIOS[i % 3]} overflow-hidden`}>
                <img
                  src={imgSrc(c.image_url)}
                  alt={c.title}
                  loading={i < 3 ? "eager" : "lazy"}
                  decoding="async"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="pt-5">
                {c.external_url && (
                  <span data-testid={`open-call-badge-${c.id}`} className="inline-block font-grotesk text-[9px] font-semibold uppercase tracking-[0.2em] text-accent border border-accent/60 px-2 py-1 mb-3">
                    Free to apply ↗
                  </span>
                )}
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-2xl leading-tight group-hover:text-accent transition-colors duration-300">{c.title}</p>
                  <ArrowUpRight size={18} className="shrink-0 mt-1 text-white/30 group-hover:text-accent transition-colors" />
                </div>
                <p className="font-sans text-sm text-white/60 mt-2">{c.brand} · {c.city}</p>
                <p className="font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-white/90 mt-2">{c.compensation || "Rate TBD"}</p>
                <Chips cats={c.categories} className="mt-3" />
              </div>
            </Link>
          </Reveal>
        </div>
      ))}
    </div>
  );
}
